import React, { useEffect, useRef, useState } from "react";
import { initHighchartsModules } from "../../../../../utils/initHighchartsModules";
import {
  Button,
  Card,
  Col,
  Dropdown,
  DropdownButton,
  Row,
} from "react-bootstrap";
import type { Options } from "highcharts";
import AccordionComponent from "../../../../../common/Accordion";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { fetchLineItemStatusPipelineData } from "../../../../../services/BillingDashboardAPI";
import { fetchTransciberStatusPipeline } from "../../../../../services/TranscriberDashboardAPI";
import MonthIntervalDropdown from "../../../../../common/MonthIntervalDropdown";

const STATUS_COLORS: Record<string, string> = {
  Overdue: "#F56565",
  "Transcription In Progress": "#60A5FA",
  "Transcription Pending": "#FBBF24",
  Exception: "#e224fb",
  Completed: "#34D399",
};

interface TranscriberPipelineProps {
  openDropdown: string | null;
  setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>;
}

const TranscriberPipeline: React.FC<TranscriberPipelineProps> = ({
  openDropdown,
  setOpenDropdown,
}) => {
  const [summaryData, setSummaryData] = useState<any | null>(null);
  const [hiddenStatuses, setHiddenStatuses] = useState<Set<string>>(new Set());
  const [monthInterval, setMonthInterval] = useState<string>("1");
  const [intervalLabel, setIntervalLabel] = useState<string>("This Month");

  useEffect(() => {
    initHighchartsModules();
  }, []);

  const fetchTranscriberPipelineData = async () => {
    try {
      const result = await fetchTransciberStatusPipeline(monthInterval);
      setSummaryData(result?.result);
    } catch (error) {
      console.error("Failed to load case overview data", error);
    }
  };

  useEffect(() => {
    fetchTranscriberPipelineData();
  }, [monthInterval]);

  const handleIntervalChange = (value: string | number, label: string) => {
    setMonthInterval(String(value));
    setIntervalLabel(label);
  };

  const normalizedData = summaryData
    ? {
        processName: summaryData.processName,
        statusCounts: JSON.parse(summaryData.statusCounts || "{}"),
      }
    : null;

  const buildChartOptions = (): Highcharts.Options => {
    if (!normalizedData) return {};

    const statusCounts = normalizedData.statusCounts;

    const series: Highcharts.SeriesOptionsType[] = Object.keys(statusCounts)
      .reverse()
      .map(
        (status): Highcharts.SeriesBarOptions => ({
          type: "bar",
          name: status,
          data: [statusCounts[status] ?? 0],
          color: STATUS_COLORS[status] || "#999",
          visible: !hiddenStatuses.has(status),
        }),
      );

    return {
      chart: {
        type: "bar",
        height: 60,
      },

      title: { text: undefined },
      exporting: { enabled: false },

      xAxis: {
        categories: ["Invoice"],
        visible: false,
      },

      yAxis: {
        min: 0,
        title: { text: undefined },
        gridLineWidth: 0,
        labels: { enabled: false },
      },

      legend: { enabled: false },

      tooltip: {
        formatter: function () {
          return `<b>${this.series.name}</b>: ${this.y}`;
        },
      },

      plotOptions: {
        bar: {
          stacking: "percent",
          pointWidth: 40,
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.Point) {
              return this.y && this.y > 0 ? this.y : "";
            },
            style: {
              color: "#fff",
              fontWeight: "bold",
              textOutline: "none",
              fontSize: "12px",
            },
          },
        },
      },

      credits: { enabled: false },
      series,
    };
  };

  const toggleStatus = (status: string) => {
    setHiddenStatuses((prev) => {
      const updated = new Set(prev);
      if (updated.has(status)) {
        updated.delete(status);
      } else {
        updated.add(status);
      }
      return updated;
    });
  };

  const [caseSummaryKey, setCaseSummaryKey] = useState<string | null>("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    if (caseSummaryKey === key) {
      return;
    }
    setCaseSummaryKey(key || "0");
  };

  const summaryAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={caseSummaryKey}
      onSelect={handleBodyAccordionSelect}
    />
  );

  const hasAnyData =
    normalizedData &&
    Object.values(normalizedData.statusCounts).some(
      (value) => Number(value) > 0,
    );

  return (
    <div className="bg-white rounded-lg shadow-md">
      {summaryAccordion([
        {
          title: (
            <div
              className="d-flex  align-items-center w-100 "
              onClick={(e) => e.stopPropagation()}
            >
              <span className="me-auto">Transcriber Status Pipeline</span>
              <div className="d-flex gap-2">
                <MonthIntervalDropdown
                  title={intervalLabel}
                  onSelect={handleIntervalChange}
                  show={openDropdown === "transcriberpipeline"}
                  onToggle={(isOpen) =>
                    setOpenDropdown(isOpen ? "transcriberpipeline" : null)
                  }
                />
              </div>
            </div>
          ),
          content: (
            <>
              {hasAnyData ? (
                <>
                  <HighchartsReact
                    highcharts={Highcharts}
                    options={buildChartOptions()}
                  />

                  {/* Legend */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      marginTop: "8px",
                    }}
                  >
                    {Object.keys(normalizedData.statusCounts).map((status) => {
                      const isHidden = hiddenStatuses.has(status);

                      return (
                        <div
                          key={status}
                          onClick={() => toggleStatus(status)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: "11px",
                            cursor: "pointer",
                            opacity: isHidden ? 0.4 : 1,
                          }}
                        >
                          <span
                            style={{
                              width: "14px",
                              height: "14px",
                              backgroundColor: STATUS_COLORS[status] || "#999",
                              marginRight: "6px",
                              borderRadius: "3px",
                            }}
                          />
                          {status}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <div className="nodata_wrapper">
                    <span className="material-symbols-outlined">
                      bar_chart_off
                    </span>{" "}
                    No Data Available
                  </div>
                </div>
              )}
            </>
          ),
        },
      ])}
    </div>
  );
};

export default TranscriberPipeline;
